/**
 * Work packages — the subcontract scope a person is given.
 *
 * Every write returns the WHOLE list, the same shape as locations and snags,
 * so a screen never has to merge a response into state it already holds. That
 * is what stops a register drifting out of step with the server after a save.
 */

import { ifMatch, request } from './client.js';

export interface WorkPackageMember {
  readonly user_id: string;
  readonly name: string | null;
  /** The role they hold ON THIS PROJECT — what decides whether the package narrows anything. */
  readonly role_key: string | null;
  readonly added_at: string;
}

export interface WorkPackageCounts {
  readonly task: number;
  readonly snag: number;
  readonly ncr: number;
  readonly inspection: number;
  readonly activity: number;
  readonly submittal: number;
  readonly deliverable: number;
}

export interface WorkPackage {
  readonly id: string;
  /** WP-0001 upwards. Allocated server-side and never reused. */
  readonly code: string;
  readonly name: string;
  readonly description: string | null;
  /** Who is doing it, in words. Not a login — they may never have one. */
  readonly contractor_name: string | null;
  readonly created_by_name: string | null;
  readonly created_at: string;
  readonly members: readonly WorkPackageMember[];
  readonly record_counts: WorkPackageCounts;
}

export interface WorkPackageInput {
  readonly name?: string;
  readonly description?: string | null;
  readonly contractor_name?: string | null;
}

const path = (projectId: string) =>
  `/v1/projects/${encodeURIComponent(projectId)}/work-packages`;

export async function listWorkPackages(projectId: string): Promise<readonly WorkPackage[]> {
  return (await request<{ data: readonly WorkPackage[] }>(path(projectId))).data;
}

export async function createWorkPackage(
  projectId: string,
  input: WorkPackageInput & { name: string },
): Promise<readonly WorkPackage[]> {
  return (
    await request<{ data: readonly WorkPackage[] }>(path(projectId), {
      method: 'POST',
      body: JSON.stringify(input),
    })
  ).data;
}

export async function updateWorkPackage(
  projectId: string,
  packageId: string,
  input: WorkPackageInput,
  /** The `record_version`… there is none on the row, so this stays unused today.
   *  Kept because the ROUTE reads `If-Match` and a caller that wants the guard
   *  should not have to reach past this module to get it. */
  expectedVersion?: string,
): Promise<readonly WorkPackage[]> {
  return (
    await request<{ data: readonly WorkPackage[] }>(
      `${path(projectId)}/${encodeURIComponent(packageId)}`,
      { method: 'PATCH', body: JSON.stringify(input), ...ifMatch(expectedVersion) },
    )
  ).data;
}

export async function deleteWorkPackage(
  projectId: string,
  packageId: string,
): Promise<readonly WorkPackage[]> {
  return (
    await request<{ data: readonly WorkPackage[] }>(
      `${path(projectId)}/${encodeURIComponent(packageId)}`,
      { method: 'DELETE' },
    )
  ).data;
}

/**
 * Put somebody on a package.
 *
 * Its own call, not a field on the edit form, because it is what DECIDES WHAT
 * THEY CAN SEE — the server audits it as its own event for the same reason.
 */
export async function assignToWorkPackage(
  projectId: string,
  packageId: string,
  userId: string,
): Promise<readonly WorkPackage[]> {
  return (
    await request<{ data: readonly WorkPackage[] }>(
      `${path(projectId)}/${encodeURIComponent(packageId)}/members`,
      { method: 'POST', body: JSON.stringify({ user_id: userId }) },
    )
  ).data;
}

/** The seven kinds of record a package can hold. `photo` is not one — see the service. */
export const FILEABLE_KINDS = [
  { type: 'task', label: 'Tasks' },
  { type: 'snag', label: 'Snags' },
  { type: 'ncr', label: 'NCRs' },
  { type: 'inspection', label: 'Inspections' },
  { type: 'activity', label: 'Activities' },
  { type: 'submittal', label: 'Submittals' },
  { type: 'deliverable', label: 'Drawings' },
] as const;

export interface FileableRecord {
  readonly id: string;
  readonly label: string;
  /** Which package it is in now. NULL is main-contractor work. */
  readonly work_package_id: string | null;
}

export async function listFileable(
  projectId: string,
  entityType: string,
): Promise<readonly FileableRecord[]> {
  return (
    await request<{ data: readonly FileableRecord[] }>(
      `/v1/projects/${encodeURIComponent(projectId)}/fileable/${encodeURIComponent(entityType)}`,
    )
  ).data;
}

/**
 * Put a record into a package, or take it out with `workPackageId: null`.
 *
 * Returns the whole package list, so the counts on screen are the server's
 * rather than something the browser worked out.
 */
export async function fileIntoWorkPackage(
  projectId: string,
  entityType: string,
  entityId: string,
  workPackageId: string | null,
): Promise<readonly WorkPackage[]> {
  return (
    await request<{ data: readonly WorkPackage[] }>(
      `/v1/projects/${encodeURIComponent(projectId)}/filing`,
      {
        method: 'POST',
        body: JSON.stringify({
          entity_type: entityType,
          entity_id: entityId,
          work_package_id: workPackageId,
        }),
      },
    )
  ).data;
}

export async function removeFromWorkPackage(
  projectId: string,
  packageId: string,
  userId: string,
): Promise<readonly WorkPackage[]> {
  return (
    await request<{ data: readonly WorkPackage[] }>(
      `${path(projectId)}/${encodeURIComponent(packageId)}/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
    )
  ).data;
}
